import { useRef, useState } from 'react';
import { getLocalItem, getSessionItem, setLocalItem, setSessionItem } from '../utils/safeStorage';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const ADMIN_ACCESS_KEY = 'adminAccessToken';
const LEGACY_ADMIN_ACCESS_KEY = 'hv_admin_token';
const BANNER_UPLOAD_PATH = '/api/admin/content/banners/upload';
const DEBUG_UPLOAD = process.env.NODE_ENV !== 'production';

const logUpload = (...args) => {
  if (DEBUG_UPLOAD) {
    console.debug('[banner-upload]', ...args);
  }
};

const parseMessage = (text, fallback) => {
  if (!text) return fallback;
  try {
    const parsed = JSON.parse(text);
    return parsed?.message || fallback;
  } catch (_) {
    return text || fallback;
  }
};

const persistAdminToken = (token) => {
  if (!token) return;
  setSessionItem(ADMIN_ACCESS_KEY, token);
  setLocalItem(ADMIN_ACCESS_KEY, token);
  setSessionItem(LEGACY_ADMIN_ACCESS_KEY, token);
  setLocalItem(LEGACY_ADMIN_ACCESS_KEY, token);
};

const readStoredAdminToken = (exclude = '') => {
  const current = String(exclude || '').trim();
  const candidates = [
    getSessionItem(ADMIN_ACCESS_KEY),
    getLocalItem(ADMIN_ACCESS_KEY),
    getSessionItem(LEGACY_ADMIN_ACCESS_KEY),
    getLocalItem(LEGACY_ADMIN_ACCESS_KEY),
  ].map((value) => String(value || '').trim());
  return candidates.find((value) => value && value !== current) || '';
};

const refreshAdminToken = async () => {
  try {
    const res = await fetch(`${API_BASE}/api/admin/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    const nextToken = data?.accessToken || data?.token || '';
    if (!nextToken) return null;
    persistAdminToken(nextToken);
    return nextToken;
  } catch (_) {
    return null;
  }
};

const resolveAdminToken = async (token) => {
  if (token) {
    persistAdminToken(token);
    return token;
  }
  const stored = readStoredAdminToken();
  if (stored) return stored;
  return refreshAdminToken();
};

const uploadFile = async (file, authToken, uploadPath, fieldName) => {
  const form = new FormData();
  form.append(fieldName, file);
  const url = `${API_BASE}${uploadPath}`;
  logUpload('request', { url, name: file?.name, size: file?.size, type: file?.type });
  const res = await fetch(url, {
    method: 'POST',
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    credentials: 'include',
    body: form,
  });
  const text = await res.text();
  logUpload('response', { status: res.status, body: text });
  if (!res.ok) {
    const err = new Error(parseMessage(text, 'Upload failed'));
    err.status = res.status;
    throw err;
  }
  const data = text ? JSON.parse(text) : {};
  const fileEntry = Array.isArray(data?.files) ? data.files[0] : null;
  const uploadedPath =
    data?.url ||
    data?.path ||
    fileEntry?.url ||
    fileEntry?.path ||
    (fileEntry?.filename ? `/uploads/${fileEntry.filename}` : '');
  if (!uploadedPath) {
    const err = new Error('Upload response missing file path');
    err.status = res.status;
    throw err;
  }
  return { ...data, path: uploadedPath, url: data?.url || uploadedPath };
};

export default function UploadDropzone({
  token,
  uploadPath = BANNER_UPLOAD_PATH,
  onUploaded,
  onUploadedMany,
  label = 'Upload images',
  accept = 'image/*',
  fieldName = 'image',
  multiple = true,
  helperText = 'Drag & drop or click to select (multiple allowed)',
}) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFiles = async (files) => {
    const list = Array.from(files);
    if (!list.length) return;
    let effectiveToken = await resolveAdminToken(token);
    logUpload(
      'selected',
      list.map((file) => ({ name: file.name, size: file.size, type: file.type }))
    );
    setUploading(true);
    setError('');
    const uploadedPaths = [];
    const errors = [];
    try {
      for (const file of list) {
        try {
          let data;
          try {
            data = await uploadFile(file, effectiveToken, uploadPath, fieldName);
          } catch (err) {
            if (err.status === 401) {
              const storedToken = readStoredAdminToken(effectiveToken);
              if (storedToken) {
                effectiveToken = storedToken;
                data = await uploadFile(file, effectiveToken, uploadPath, fieldName);
              } else {
                const refreshed = await refreshAdminToken();
                if (refreshed) {
                  effectiveToken = refreshed;
                  data = await uploadFile(file, effectiveToken, uploadPath, fieldName);
                } else {
                  throw err;
                }
              }
            } else {
              throw err;
            }
          }
          uploadedPaths.push(data.path);
          onUploaded?.(data.path);
        } catch (err) {
          errors.push(err.message || 'Upload failed');
        }
      }
      if (uploadedPaths.length) {
        onUploadedMany?.(uploadedPaths);
      }
      if (errors.length) {
        setError(errors[0]);
      }
    } finally {
      setUploading(false);
      setDrag(false);
    }
  };

  return (
    <div
      className={`border-2 border-dashed rounded-xl px-4 py-6 text-center cursor-pointer ${drag ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        handleFiles(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
    >
      <div className="text-sm text-slate-600">{label}</div>
      <div className="text-xs text-slate-400 mt-1">
        {uploading ? 'Uploading...' : helperText}
      </div>
      {error && <div className="mt-2 text-xs text-red-500">{error}</div>}
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        ref={inputRef}
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}


